/**
 * English: Secondary view tabs — read-only views alongside the main chat.
 * Thai: แท็บมุมมองรอง — มุมมองอ่านอย่างเดียวคู่กับแชทหลัก
 * chatTarget === 'master'  → Discussion + Stations tabs
 * chatTarget === 'manager' → Team Discussion tab
 * chatTarget starts with 'device_' → no tabs
 */
import {
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

export type SecondaryTab = 'discussion' | 'stations' | 'teamDiscussion' | 'personalities';

interface TabDef {
  key: SecondaryTab;
  label: string;
  hint: string;
  Icon: typeof ArrowsRightLeftIcon;
}

const MASTER_SECONDARY: TabDef[] = [
  {
    key: 'discussion',
    label: 'สนทนา Manager',
    hint: 'การสนทนาระหว่างผู้จัดการสถานี (A2A)',
    Icon: ArrowsRightLeftIcon,
  },
  {
    key: 'stations',
    label: 'รายละเอียดสถานี',
    hint: 'รายละเอียดข้อมูลในสถานีย่อย',
    Icon: BuildingOffice2Icon,
  },
];

const MANAGER_SECONDARY: TabDef[] = [];

export function getSecondaryTabs(chatTarget: string): TabDef[] {
  if (chatTarget === 'master') return MASTER_SECONDARY;
  if (chatTarget === 'manager') return MANAGER_SECONDARY;
  return [];
}

interface SecondaryTabsProps {
  chatTarget: string;
  activeSecondary: SecondaryTab | null;
  onTabChange: (tab: SecondaryTab | null) => void;
}

export default function SecondaryTabs({ chatTarget, activeSecondary, onTabChange }: SecondaryTabsProps) {
  const tabs = getSecondaryTabs(chatTarget);
  if (tabs.length === 0) return null;

  const prefixLabel = chatTarget === 'master' ? 'มุมมองสถานี:' : 'มุมมองขยาย:';

  return (
    <div className="flex items-center gap-0.5 border-b border-app-border bg-app-muted px-2 py-1 shrink-0 overflow-x-auto">
      <span className="text-[10px] text-app-text-dim font-medium mr-1 shrink-0">{prefixLabel}</span>
      {tabs.map(({ key, label, hint, Icon }) => (
        <button
          key={key}
          type="button"
          title={hint}
          onClick={() => onTabChange(activeSecondary === key ? null : key)}
          className={`flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors whitespace-nowrap ${
            activeSecondary === key
              ? 'bg-app-accent/15 text-app-accent'
              : 'text-app-text-dim hover:text-app-text hover:bg-app-muted'
          }`}
        >
          <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
          {label}
        </button>
      ))}
    </div>
  );
}
